import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./rules.css";
import { setBackButtonUrl } from "../uiSlice";
import { LOGIN_BACK_PATH } from "../gameSetupStorage";

const LEAF_ICONS = [
  { icon: "fa-people-group", color: "#7c3aed" },
  { icon: "fa-scale-balanced", color: "#2563eb" },
  { icon: "fa-heart", color: "#0d9488" },
  { icon: "fa-handshake", color: "#f97316" },
  { icon: "fa-lightbulb", color: "#f59e0b" },
];

const CIRCLE_RING_COLORS = [
  ["#7c3aed", "#db2777", "#0d9488"],
  ["#ef4444", "#f97316", "#2563eb"],
  ["#a3a635", "#16a34a", "#2563eb"],
  ["#2563eb", "#7c3aed", "#db2777"],
  ["#f97316", "#ef4444", "#7c3aed"],
  ["#0d9488", "#2563eb", "#a3a635"],
];

/* Each loop is its own independent SVG in a 0-100 local viewBox (cx=50, cy=50, r=47),
 * matching the CSS layout's clamp(200px,22vw,300px) width + clamp(-48px..-70px) overlap
 * margin (adjacent loop centers sit RC_NEIGHBOR_OFFSET local units apart). Rather than
 * drawing full circles and clipping away the overlap (which leaves anti-aliasing seams),
 * each loop is drawn as ONE OR TWO explicit arc(s) that stop exactly at the two points
 * where this circle's boundary would cross its neighbor's — computed directly from
 * circle-circle intersection geometry — so only the true outer silhouette is ever drawn. */
const RC_RADIUS = 47;
const RC_STROKE = 2; // ≈4-6px at the loop's 200-300px rendered width
const RC_CENTER = 50;
/* CSS ties each loop's overlap margin to its own width via calc(var(--rc-w) * -0.18), which
 * is exactly the ratio RC_NEIGHBOR_OFFSET encodes (1 - 2*0.18 = 0.64) — so adjacent loops'
 * arc endpoints already land on the true geometric intersection with no drift. Keep these
 * two values (here and in rules.css) in sync: a shallower ratio means less of each loop is
 * pinched away, so the loops read as fuller circles instead of flattened ovals. */
const RC_NEIGHBOR_OFFSET = 64;
/* No extra overlap margin is needed on top of that exact match — a nonzero value here shows
 * up as a small crossed "spike" past the pinch point instead of a clean meeting. */
const RC_ARC_OVERLAP = 0;

/** Closed circle outline as two arcs (used only for a single, neighborless loop). */
function circleSubpath(cx, cy, r) {
  return `M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy} Z`;
}

/**
 * Visible arc(s) for one loop given whether it has a left and/or right neighbor.
 * A loop with one neighbor draws a single ~247° major arc (a "V" pinch on that side).
 * A loop with neighbors on both sides draws a top cap + bottom cap, pinched on both sides.
 */
function buildLoopArcs(hasLeft, hasRight) {
  const r = RC_RADIUS;
  const cx = RC_CENTER;
  const cy = RC_CENTER;
  const half = RC_NEIGHBOR_OFFSET / 2 + RC_ARC_OVERLAP;
  const h = Math.sqrt(Math.max(0, r * r - half * half));

  if (!hasLeft && !hasRight) {
    return [circleSubpath(cx, cy, r)];
  }

  const rightTop = [cx + half, cy - h];
  const rightBot = [cx + half, cy + h];
  const leftTop = [cx - half, cy - h];
  const leftBot = [cx - half, cy + h];

  if (hasRight && !hasLeft) {
    return [`M ${rightTop[0]} ${rightTop[1]} A ${r} ${r} 0 1 0 ${rightBot[0]} ${rightBot[1]}`];
  }

  if (hasLeft && !hasRight) {
    return [`M ${leftTop[0]} ${leftTop[1]} A ${r} ${r} 0 1 1 ${leftBot[0]} ${leftBot[1]}`];
  }

  return [
    `M ${leftTop[0]} ${leftTop[1]} A ${r} ${r} 0 0 1 ${rightTop[0]} ${rightTop[1]}`,
    `M ${leftBot[0]} ${leftBot[1]} A ${r} ${r} 0 0 0 ${rightBot[0]} ${rightBot[1]}`,
  ];
}

export default function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, user } = useSelector((s) => s.auth);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  useEffect(() => {
    dispatch(setBackButtonUrl(LOGIN_BACK_PATH));
  }, [dispatch, status, user]);

  /* Unlock document scroll so logo + Back scroll away with the rules list.
   * Login CSS otherwise keeps body.common-bg overflow:hidden on mobile portrait. */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const snap = [];
    const setImp = (el, prop, value) => {
      if (!el) return;
      snap.push([
        el,
        prop,
        el.style.getPropertyValue(prop),
        el.style.getPropertyPriority(prop),
      ]);
      el.style.setProperty(prop, value, "important");
    };

    html.classList.add("sl-rules-open");
    body.classList.add("sl-rules-open");

    setImp(html, "height", "auto");
    setImp(html, "max-height", "none");
    setImp(html, "overflow-x", "hidden");
    setImp(html, "overflow-y", "auto");
    setImp(html, "overflow", "auto");

    setImp(body, "height", "auto");
    setImp(body, "min-height", "100%");
    setImp(body, "max-height", "none");
    setImp(body, "overflow", "visible");
    setImp(body, "overflow-x", "hidden");
    setImp(body, "overflow-y", "visible");
    setImp(body, "position", "static");
    setImp(body, "background-attachment", "scroll");
    setImp(body, "touch-action", "pan-y");

    setImp(root, "height", "auto");
    setImp(root, "min-height", "100%");
    setImp(root, "max-height", "none");
    setImp(root, "overflow", "visible");

    return () => {
      html.classList.remove("sl-rules-open");
      body.classList.remove("sl-rules-open");
      snap.forEach(([el, prop, value, priority]) => {
        if (value) el.style.setProperty(prop, value, priority || undefined);
        else el.style.removeProperty(prop);
      });
    };
  }, []);

  const rules = Array.isArray(themeData?.rules)
    ? themeData.rules.map((r) => String(r || "").trim()).filter(Boolean)
    : [];

  const introText = String(themeData?.landing_page_title ?? "").trim();
  const titleColor = String(themeData?.title_color || "#16213e").trim();

  const ruleItems = rules.length ? rules : ["No rules defined"];

  if (themeStatus === "loading" && !themeData) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div className="sl-rules-page">
      <div className="sl-rules-scene" aria-hidden="true">
        <div className="sl-rules-branch">
          <span className="sl-rules-stem" />
          {LEAF_ICONS.map(({ icon, color }, i) => (
            <span
              key={icon}
              className="sl-rules-leaf"
              style={{ "--leaf-color": color, "--leaf-i": i }}
            >
              <i className={`fa-solid ${icon}`} aria-hidden="true" />
            </span>
          ))}
        </div>
      </div>

      <div className="sl-rules-content">
        <div className="sl-rules-pill sl-rules-pill--title">
          <span className="sl-rules-pill-inner">How to Play</span>
        </div>

        <ul className="sl-rules-chain">
          {ruleItems.map((rule, i, arr) => {
            const [c1, c2, c3] = CIRCLE_RING_COLORS[i % CIRCLE_RING_COLORS.length];
            const gradId = `sl-rules-ring-grad-${i}`;
            const arcs = buildLoopArcs(i > 0, i < arr.length - 1);
            return (
              <li key={`${i}-${rule.slice(0, 12)}`} className="sl-rules-circle">
                <svg
                  className="sl-rules-circle-ring"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor={c1} />
                      <stop offset="50%" stopColor={c2} />
                      <stop offset="100%" stopColor={c3} />
                    </linearGradient>
                  </defs>
                  {arcs.map((d, ai) => (
                    <path
                      key={ai}
                      d={d}
                      fill="none"
                      stroke={`url(#${gradId})`}
                      strokeWidth={RC_STROKE}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ))}
                </svg>
                <span className="sl-rules-circle-text" style={{ color: titleColor }}>
                  {rule}
                </span>
                {i < arr.length - 1 ? (
                  <span className="sl-rules-chevron" aria-hidden="true">
                    &#8250;
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          className="sl-rules-pill sl-rules-pill--next"
          onClick={() => navigate("/game")}
        >
          <span className="sl-rules-pill-inner">Next</span>
        </button>
      </div>

      <div className="sl-rotate-overlay" aria-hidden>
        Please rotate your device for the best experience
      </div>
    </div>
  );
}
